import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, Plus, Phone, Mail, Edit, Trash2, AlertTriangle, CheckCircle2, Clock, User, Filter, Lock } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Clientes() {
  const [clientes, setClientes] = useState([]);
  const [tratos, setTratos] = useState([]);
  const [lotes, setLotes] = useState([]);
  const [fraccionamientos, setFraccionamientos] = useState([]);
  // Recuperar filtro guardado de localStorage
  const [selectedFraccId, setSelectedFraccId] = useState(() => {
    return localStorage.getItem('filtro_fraccionamiento') || 'todos';
  });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCliente, setEditingCliente] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  
  // Estado de permisos
  const [userRole, setUserRole] = useState('owner');
  const canWrite = userRole === 'owner' || userRole === 'admin';
  
  // Guardar filtro en localStorage cuando cambie
  const handleFraccFilter = (value) => {
    setSelectedFraccId(value);
    localStorage.setItem('filtro_fraccionamiento', value);
  };
  
  const [formData, setFormData] = useState({
    nombre_completo: '',
    telefono: '',
    email: '',
    identificacion: ''
  });

  useEffect(() => {
    fetchData();
    fetchUserRole();
  }, []);

  const fetchUserRole = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' });
      if (response.ok) {
        const userData = await response.json();
        setUserRole(userData.account_role || 'owner');
      }
    } catch (error) {
      console.error('Error al obtener usuario');
    }
  };

  const fetchData = async () => {
    try {
      const [clientesRes, tratosRes, lotesRes, fraccsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/clientes/con-estado`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/tratos`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/lotes`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/fraccionamientos`, { credentials: 'include' })
      ]);
      
      if (!clientesRes.ok) throw new Error('Error al cargar datos');
      
      const [clientesData, tratosData, lotesData, fraccsData] = await Promise.all([
        clientesRes.json(),
        tratosRes.ok ? tratosRes.json() : [],
        lotesRes.ok ? lotesRes.json() : [],
        fraccsRes.ok ? fraccsRes.json() : []
      ]);
      
      setClientes(Array.isArray(clientesData) ? clientesData : []);
      setTratos(Array.isArray(tratosData) ? tratosData : []);
      setLotes(Array.isArray(lotesData) ? lotesData : []);
      setFraccionamientos(Array.isArray(fraccsData) ? fraccsData : []);
    } catch (error) {
      toast.error('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  const getEstadoRiesgoBadge = (cliente) => {
    const estados = {
      sin_tratos: { bg: 'bg-zinc-700', text: 'text-zinc-400', label: 'Sin tratos' },
      al_corriente: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Al corriente' },
      alerta: { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'Alerta' },
      critico: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Crítico' }
    };
    
    const estado = estados[cliente.estado_riesgo] || estados.sin_tratos;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold rounded-full ${estado.bg} ${estado.text}`}>
        {estado.label}
      </span>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingCliente
        ? `${BACKEND_URL}/api/clientes/${editingCliente.cliente_id}`
        : `${BACKEND_URL}/api/clientes`;
      
      const payload = {
        nombre_completo: formData.nombre_completo
      };
      if (formData.telefono && formData.telefono.trim()) {
        payload.telefono = formData.telefono.trim();
      }
      if (formData.email && formData.email.trim()) {
        payload.email = formData.email.trim();
      }
      if (formData.identificacion && formData.identificacion.trim()) {
        payload.identificacion = formData.identificacion.trim();
      }
      
      const response = await fetch(url, {
        method: editingCliente ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al guardar cliente');
      }
      
      toast.success(editingCliente ? 'Cliente actualizado' : 'Cliente creado');
      setDialogOpen(false);
      setEditingCliente(null);
      setFormData({ nombre_completo: '', telefono: '', email: '', identificacion: '' });
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleEdit = (cliente) => {
    setEditingCliente(cliente);
    setFormData({
      nombre_completo: cliente.nombre_completo || '',
      telefono: cliente.telefono || '',
      email: cliente.email || '',
      identificacion: cliente.identificacion || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteDialog.id) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/clientes/${deleteDialog.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al eliminar cliente');
      }
      
      toast.success('Cliente eliminado correctamente');
      setDeleteDialog({ open: false, id: null });
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Filtrar clientes por fraccionamiento (clientes que tienen tratos en ese fraccionamiento)
  const clientesFiltrados = selectedFraccId === 'todos' 
    ? clientes 
    : clientes.filter(cliente => {
        // Buscar tratos del cliente
        const tratosDelCliente = tratos.filter(t => t.cliente_id === cliente.cliente_id);
        // Verificar si algún trato tiene lotes del fraccionamiento seleccionado
        return tratosDelCliente.some(trato => {
          const lotesIds = trato.lotes_ids || (trato.lote_id ? [trato.lote_id] : []);
          return lotesIds.some(loteId => {
            const lote = lotes.find(l => l.lote_id === loteId);
            return lote?.fraccionamiento_id === selectedFraccId;
          });
        });
      });

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="clientes-page" className="min-h-screen bg-zinc-950 p-8">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-10">
        <div>
          <h1 className="text-4xl font-bold text-white tracking-tight">Clientes</h1>
          <p className="text-zinc-400 mt-2">Administra la información de tus clientes</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Filtro por Fraccionamiento */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-zinc-500" />
            <Select value={selectedFraccId} onValueChange={handleFraccFilter}>
              <SelectTrigger className="w-[200px] bg-zinc-900 border-zinc-700 text-white">
                <SelectValue placeholder="Filtrar por..." />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700">
                <SelectItem value="todos" className="text-white hover:bg-zinc-800">Todos los fraccionamientos</SelectItem>
                {fraccionamientos.map((f) => (
                  <SelectItem key={f.fraccionamiento_id} value={f.fraccionamiento_id} className="text-white hover:bg-zinc-800">
                    {f.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) {
              setEditingCliente(null);
              setFormData({ nombre_completo: '', telefono: '', email: '', identificacion: '' });
            }
          }}>
            {canWrite ? (
              <DialogTrigger asChild>
                <Button data-testid="create-cliente-button" className="bg-amber-500 hover:bg-amber-400 text-black font-bold">
                  <Plus className="w-4 h-4 mr-2" />
                  Nuevo Cliente
                </Button>
              </DialogTrigger>
            ) : (
              <Button disabled className="bg-zinc-800 text-zinc-500 cursor-not-allowed">
                <Lock className="w-4 h-4 mr-2" />
                Solo Lectura
              </Button>
            )}
            <DialogContent className="sm:max-w-md bg-zinc-900 border-zinc-800 text-white">
              <DialogHeader>
                <DialogTitle className="text-white">{editingCliente ? 'Editar Cliente' : 'Nuevo Cliente'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-zinc-300">Nombre Completo *</Label>
                  <Input
                    data-testid="input-nombre"
                    value={formData.nombre_completo}
                    onChange={(e) => setFormData({...formData, nombre_completo: e.target.value})}
                    required
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Teléfono</Label>
                  <Input
                    data-testid="input-telefono"
                    value={formData.telefono}
                    onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Email</Label>
                  <Input
                    data-testid="input-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Identificación</Label>
                  <Input
                    data-testid="input-identificacion"
                    value={formData.identificacion}
                    onChange={(e) => setFormData({...formData, identificacion: e.target.value})}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <Button type="submit" className="w-full bg-amber-500 hover:bg-amber-400 text-black font-bold">
                  {editingCliente ? 'Actualizar' : 'Crear Cliente'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {clientesFiltrados.length === 0 ? (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-16 text-center">
          <Users className="w-16 h-16 mx-auto mb-4 text-zinc-700" />
          <h3 className="text-lg font-bold text-white mb-2">
            {clientes.length === 0 ? 'No hay clientes registrados' : 'No hay clientes en este fraccionamiento'}
          </h3>
          <p className="text-zinc-500 mb-4">
            {clientes.length === 0 ? 'Crea el primer cliente para comenzar' : 'Selecciona otro fraccionamiento o crea un nuevo cliente'}
          </p>
          {canWrite ? (
            <Button onClick={() => setDialogOpen(true)} className="bg-amber-500 hover:bg-amber-400 text-black font-bold">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Cliente
            </Button>
          ) : (
            <Button disabled className="bg-zinc-800 text-zinc-500 cursor-not-allowed">
              <Lock className="w-4 h-4 mr-2" />
              Solo Lectura
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {clientesFiltrados.map((cliente) => (
            <div 
              key={cliente.cliente_id}
              data-testid={`cliente-card-${cliente.cliente_id}`}
              className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 hover:border-zinc-700 transition-colors"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-amber-500/10 rounded-lg">
                    <User className="w-6 h-6 text-amber-500" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-white">{cliente.nombre_completo}</h3>
                    {getEstadoRiesgoBadge(cliente)}
                  </div>
                </div>
              </div>
              
              {/* Mostrar fraccionamientos donde compró */}
              {(() => {
                const tratosDelCliente = tratos.filter(t => t.cliente_id === cliente.cliente_id);
                const fraccsDelCliente = new Set();
                tratosDelCliente.forEach(trato => {
                  const lotesIds = trato.lotes_ids || (trato.lote_id ? [trato.lote_id] : []);
                  lotesIds.forEach(loteId => {
                    const lote = lotes.find(l => l.lote_id === loteId);
                    if (lote) {
                      const fracc = fraccionamientos.find(f => f.fraccionamiento_id === lote.fraccionamiento_id);
                      if (fracc) fraccsDelCliente.add(fracc.nombre);
                    }
                  });
                });
                const fraccsArray = Array.from(fraccsDelCliente);
                
                if (fraccsArray.length > 0) {
                  return (
                    <div className="mb-4">
                      <p className="text-xs text-zinc-500 mb-2">Compró en:</p>
                      <div className="flex flex-wrap gap-1">
                        {fraccsArray.map((nombre, idx) => (
                          <span key={idx} className="px-2 py-1 bg-amber-500/20 text-amber-400 text-xs font-medium rounded">
                            {nombre}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
              
              <div className="space-y-2 mb-4">
                {cliente.telefono && (
                  <div className="flex items-center gap-2 text-sm text-zinc-400">
                    <Phone className="w-4 h-4 text-zinc-600" />
                    <span>{cliente.telefono}</span>
                  </div>
                )}
                {cliente.email && (
                  <div className="flex items-center gap-2 text-sm text-zinc-400">
                    <Mail className="w-4 h-4 text-zinc-600" />
                    <span>{cliente.email}</span>
                  </div>
                )}
                {cliente.identificacion && (
                  <p className="text-xs text-zinc-500">ID: {cliente.identificacion}</p>
                )}
              </div>
              
              {cliente.resumen_financiero && (
                <div className="grid grid-cols-2 gap-3 p-3 bg-zinc-800 rounded-lg mb-4">
                  <div>
                    <p className="text-xs text-zinc-500">Tratos</p>
                    <p className="font-mono font-bold text-white">{cliente.resumen_financiero.total_tratos || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500">Deuda</p>
                    <p className={`font-mono font-bold ${(cliente.resumen_financiero.deuda_total || 0) > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                      ${(cliente.resumen_financiero.deuda_total || 0).toLocaleString('es-MX')}
                    </p>
                  </div>
                </div>
              )}
              
              <div className="flex gap-2">
                {canWrite ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                      onClick={() => handleEdit(cliente)}
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Editar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                      onClick={() => setDeleteDialog({ open: true, id: cliente.cliente_id })}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </>
                ) : (
                  <span className="text-xs text-zinc-500 flex items-center gap-1">
                    <Lock className="w-3 h-3" /> Solo lectura
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* AlertDialog Eliminar */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({...deleteDialog, open})}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">¿Eliminar este cliente?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Esta acción no se puede deshacer. Los tratos asociados al cliente permanecerán.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
